import type { AxisPreviewItem, ShowTopStats } from 'components/molecules/PackArchitecture/axisConfiguration.utils';
import type { KpiDataRange } from 'components/molecules/PackArchitecture/kpiAxisConfiguration.utils';
import { DEFAULT_KPI_DATA_RANGE } from 'constants/ppa';
import {
    extractKpiAxisBoundsFromChartData,
    toKpiDataRange,
} from './packArchitectureChartKpiAxis.helpers';
export { extractKpiAxisBoundsFromChartData, extractKpiAxisConfigFromChartData } from './packArchitectureChartKpiAxis.helpers';
import type { SalesKpiIds } from './packArchitectureMeta.helpers';
import type {
    PpaChartBubbleNode,
    PpaChartDimensionNode,
    PpaChartKpi,
    PpaChartLegacyChartData,
    PpaChartResponse,
    PpaChartResponseBody,
    PpaTableRow,
} from './pricePackArchitecture.types';

const DEFAULT_SALES_KPI_IDS: SalesKpiIds = {
    valueSales: 'total_value',
    volumeSales: 'total_eq_volume',
};

const VALUE_SALES_FALLBACK_IDS = ['total_value', 'value_sales', 'ps_value_sales'];
const VOLUME_SALES_FALLBACK_IDS = ['total_eq_volume', 'volume_sales', 'ps_volume_sales'];

const normalizeAttributeKey = (value?: string) => value?.trim().toLowerCase() ?? '';

const normalizeKpiId = (value?: string) => value?.trim().toLowerCase() ?? '';

const toFiniteNumber = (value: unknown): number => {
    if (typeof value === 'number' && Number.isFinite(value)) {
        return value;
    }
    if (typeof value === 'string' && value.trim() !== '') {
        const parsed = Number(value);
        if (Number.isFinite(parsed)) {
            return parsed;
        }
    }
    return 0;
};

const isDimensionNode = (value: unknown): value is PpaChartDimensionNode => {
    if (!value || typeof value !== 'object') return false;
    const node = value as PpaChartDimensionNode;
    return Boolean(node.ex_id || node.description || node.attribute_type || node.dimension);
};

const isBubbleNode = (value: unknown): value is PpaChartBubbleNode => {
    if (!value || typeof value !== 'object') return false;
    const node = value as PpaChartBubbleNode;
    return Boolean(node.kpis?.length || node.description || node.ex_id);
};

const isDimensionNodeArray = (value: unknown): value is PpaChartDimensionNode[] =>
    Array.isArray(value) && value.length > 0 && isDimensionNode(value[0]);

const asResponseBody = (
    response: PpaChartResponse | null | undefined,
): PpaChartResponseBody | null => {
    if (!response || Array.isArray(response)) return null;
    return response;
};

const collectDimensionNodeCandidates = (response: PpaChartResponse): unknown[] => {
    if (Array.isArray(response)) {
        return [response];
    }

    const candidates: unknown[] = [
        response.chart_data,
        response.data,
    ];

    const chartData = response.chart_data;
    if (chartData && typeof chartData === 'object' && !Array.isArray(chartData)) {
        candidates.push((chartData as { data?: unknown }).data);
    }

    return candidates;
};

const buildKpiIdCandidates = (primaryId: string, fallbackIds: string[]): string[] => {
    const normalizedPrimary = normalizeKpiId(primaryId);
    const candidates = [primaryId, normalizedPrimary, ...fallbackIds];

    if (normalizedPrimary.includes('value') && normalizedPrimary.includes('sales')) {
        candidates.push(...VALUE_SALES_FALLBACK_IDS);
    }
    if (normalizedPrimary.includes('volume') && normalizedPrimary.includes('sales')) {
        candidates.push(...VOLUME_SALES_FALLBACK_IDS);
    }
    if (normalizedPrimary === 'total_value') {
        candidates.push(...VALUE_SALES_FALLBACK_IDS);
    }
    if (normalizedPrimary === 'total_eq_volume') {
        candidates.push(...VOLUME_SALES_FALLBACK_IDS);
    }

    return [...new Set(candidates.filter(Boolean))];
};

const KPI_TARGET_ALIAS_MATCHERS: Record<string, (entryId: string) => boolean> = {
    total_value: (entryId) => entryId === 'value_sales' || entryId.endsWith('_value_sales'),
    total_eq_volume: (entryId) => entryId === 'volume_sales' || entryId.endsWith('_volume_sales'),
};

const idsMatchDirectly = (entryId: string, target: string) =>
    entryId === target || entryId.endsWith(`_${target}`) || target.endsWith(`_${entryId}`);

const idsMatchSalesSubtype = (entryId: string, target: string) =>
    (target.includes('value_sales') && entryId.includes('value_sales')) ||
    (target.includes('volume_sales') && entryId.includes('volume_sales'));

const kpiMatchesCandidate = (entry: PpaChartKpi, candidate: string) => {
    const entryId = normalizeKpiId(entry.kpi_id ?? (entry as { id?: string }).id);
    const target = normalizeKpiId(candidate);
    if (!entryId || !target) return false;

    if (idsMatchDirectly(entryId, target)) return true;

    const aliasMatcher = KPI_TARGET_ALIAS_MATCHERS[target];
    if (aliasMatcher?.(entryId)) return true;

    return idsMatchSalesSubtype(entryId, target);
};

const findKpi = (kpis: PpaChartKpi[] | undefined, kpiIds: string | string[]): PpaChartKpi | undefined => {
    const candidates = Array.isArray(kpiIds) ? kpiIds : buildKpiIdCandidates(kpiIds, []);
    for (const candidate of candidates) {
        const kpi = kpis?.find((entry) => kpiMatchesCandidate(entry, candidate));
        if (kpi) return kpi;
    }
    return undefined;
};

const hasKpiMatch = (kpis: PpaChartKpi[] | undefined, kpiIds: string | string[]) =>
    Boolean(findKpi(kpis, kpiIds));

const getKpiRawValue = (kpis: PpaChartKpi[] | undefined, kpiIds: string | string[]): number => {
    const kpi = findKpi(kpis, kpiIds);
    if (!kpi) return 0;

    if (typeof kpi.raw_value === 'number' && Number.isFinite(kpi.raw_value)) {
        return kpi.raw_value;
    }
    return toFiniteNumber(kpi.value);
};

const isOwnDimensionNode = (node: PpaChartDimensionNode): boolean => {
    if (node.is_own) return true;

    const ownKpi = node.kpis?.find((kpi) => kpi.kpi_id === 'is_own' || kpi.kpi_id === 'own_vendor');
    if (ownKpi?.value === true || ownKpi?.value === 'true' || ownKpi?.raw_value === 1) {
        return true;
    }

    return false;
};

const sumNestedKpi = (node: PpaChartDimensionNode, kpiIds: string | string[]): number => {
    let total = 0;

    for (const entry of node.data ?? []) {
        if (isBubbleNode(entry)) {
            total += getKpiRawValue(entry.kpis, kpiIds);
            continue;
        }

        for (const item of entry.data ?? []) {
            if (isBubbleNode(item)) {
                total += getKpiRawValue(item.kpis, kpiIds);
            }
        }
    }

    return total;
};

const resolveSalesMetric = (
    node: PpaChartDimensionNode,
    primaryId: string,
    fallbackIds: string[],
): number => {
    const kpiIds = buildKpiIdCandidates(primaryId, fallbackIds);
    const directValue = getKpiRawValue(node.kpis, kpiIds);
    const nestedValue = sumNestedKpi(node, kpiIds);

    if (hasKpiMatch(node.kpis, kpiIds)) {
        return directValue > 0 ? directValue : nestedValue;
    }

    return nestedValue > 0 ? nestedValue : directValue;
};

const OTHER_GROUP_EX_ID = '__other__';

const isOtherGroupNode = (node: PpaChartDimensionNode): boolean =>
    node.ex_id?.trim().toLowerCase() === OTHER_GROUP_EX_ID;

const toMemberNames = (node: PpaChartDimensionNode): string[] =>
    (node.members ?? [])
        .map((member) => member.description?.trim() || member.ex_id?.trim())
        .filter((name): name is string => Boolean(name));

const toPreviewItem =
    (salesKpiIds: SalesKpiIds) =>
    (node: PpaChartDimensionNode): AxisPreviewItem | null => {
        const name = node.description?.trim() || node.ex_id?.trim();
        if (!name) return null;

        const item: AxisPreviewItem = {
            name,
            valueSales: resolveSalesMetric(node, salesKpiIds.valueSales, VALUE_SALES_FALLBACK_IDS),
            volumeSales: resolveSalesMetric(node, salesKpiIds.volumeSales, VOLUME_SALES_FALLBACK_IDS),
        };

        if (isOwnDimensionNode(node)) {
            item.isOwn = true;
        }

        if (isOtherGroupNode(node)) {
            item.isOtherGroup = true;
            item.members = toMemberNames(node);
        }

        return item;
    };

const resolveTableMetric = (row: PpaTableRow, primaryId: string, fallbackIds: string[]) => {
    const candidates = buildKpiIdCandidates(primaryId, fallbackIds);
    for (const candidate of candidates) {
        if (candidate in row) {
            return toFiniteNumber(row[candidate]);
        }
    }
    return 0;
};

const buildTableMetricsLookup = (tableData: PpaTableRow[] | undefined, salesKpiIds: SalesKpiIds) => {
    const lookup = new Map<string, { valueSales: number; volumeSales: number }>();

    for (const row of tableData ?? []) {
        const name = row.group?.trim();
        if (!name) continue;

        lookup.set(name, {
            valueSales: resolveTableMetric(row, salesKpiIds.valueSales, VALUE_SALES_FALLBACK_IDS),
            volumeSales: resolveTableMetric(row, salesKpiIds.volumeSales, VOLUME_SALES_FALLBACK_IDS),
        });
    }

    return lookup;
};

const buildPreviewItemsFromLegacyChartData = (
    legacy: PpaChartLegacyChartData,
    salesKpiIds: SalesKpiIds,
    tableData?: PpaTableRow[],
): AxisPreviewItem[] => {
    const tableMetrics = buildTableMetricsLookup(tableData, salesKpiIds);
    const names: string[] = [];

    if (legacy.x_axis?.values?.length) {
        names.push(...legacy.x_axis.values);
    } else {
        const seen = new Set<string>();
        for (const group of legacy.bubble_groups ?? []) {
            for (const item of group.items ?? []) {
                const name = item.x_value?.trim();
                if (name && !seen.has(name)) {
                    seen.add(name);
                    names.push(name);
                }
            }
        }
    }

    return names.map((name) => {
        const metrics = tableMetrics.get(name);
        return {
            name,
            valueSales: metrics?.valueSales ?? 0,
            volumeSales: metrics?.volumeSales ?? 0,
        };
    });
};

export const extractChartDimensionNodes = (
    response: PpaChartResponse | undefined | null,
): PpaChartDimensionNode[] => {
    if (!response) return [];

    for (const candidate of collectDimensionNodeCandidates(response)) {
        if (isDimensionNodeArray(candidate)) {
            return candidate;
        }
    }

    return [];
};

export const hasPpaChartData = (response: PpaChartResponse | undefined | null): boolean => {
    if (!response) return false;

    if (extractChartDimensionNodes(response).length > 0) return true;

    const body = asResponseBody(response);
    const chartData = body?.chart_data;
    if (chartData && !Array.isArray(chartData)) {
        return Boolean(chartData.bubble_groups?.length || chartData.x_axis?.values?.length);
    }

    return false;
};

const KPI_RANGE_FALLBACK_IDS: Record<string, string[]> = {
    base_price_per_unit: ['ps_base_price_per_unit', 'base_price_per_unit'],
    base_price_per_eq: ['ps_base_price_per_eq', 'base_price_per_eq'],
    average_price_per_unit: ['ps_average_price_per_unit', 'average_price_per_unit'],
    average_price_per_eq: ['ps_average_price_per_eq', 'average_price_per_eq'],
};

const buildKpiRangeIdCandidates = (kpiId: string): string[] => {
    const normalized = normalizeKpiId(kpiId);
    const aliases = KPI_RANGE_FALLBACK_IDS[normalized] ?? [];
    return buildKpiIdCandidates(kpiId, aliases);
};

const parseBinRangeFromLabel = (label: string): [number, number] | null => {
    const match = label.match(/\$?([\d,]+(?:\.\d+)?)\s*[-–]\s*\$?([\d,]+(?:\.\d+)?)/);
    if (!match) return null;

    const start = Number(match[1].replace(/,/g, ''));
    const end = Number(match[2].replace(/,/g, ''));
    if (!Number.isFinite(start) || !Number.isFinite(end)) return null;

    return [Math.min(start, end), Math.max(start, end)];
};

const collectKpiNumericValues = (
    response: PpaChartResponse | null | undefined,
    kpiId: string,
): number[] => {
    if (!response || !kpiId) return [];

    const values: number[] = [];
    const kpiIds = buildKpiRangeIdCandidates(kpiId);
    const addValue = (value: unknown) => {
        if (typeof value === 'number' && Number.isFinite(value)) {
            values.push(value);
        }
    };

    const visitKpis = (kpis?: PpaChartKpi[]) => {
        const kpi = findKpi(kpis, kpiIds);
        if (!kpi) return;

        if (typeof kpi.raw_value === 'number' && Number.isFinite(kpi.raw_value)) {
            values.push(kpi.raw_value);
            return;
        }

        const parsed = toFiniteNumber(kpi.value);
        if (Number.isFinite(parsed)) values.push(parsed);
    };

    const visitBubbleNode = (node: PpaChartBubbleNode) => {
        visitKpis(node.kpis);
    };

    const visitDimensionNode = (node: PpaChartDimensionNode) => {
        visitKpis(node.kpis);

        const label = node.description?.trim() || node.ex_id?.trim() || '';
        const binRange = parseBinRangeFromLabel(label);
        if (binRange) {
            values.push(binRange[0], binRange[1]);
        }

        for (const entry of node.data ?? []) {
            if (isBubbleNode(entry)) {
                visitBubbleNode(entry);
                continue;
            }
            for (const item of entry.data ?? []) {
                if (isBubbleNode(item)) visitBubbleNode(item);
            }
        }
    };

    for (const node of extractChartDimensionNodes(response)) {
        visitDimensionNode(node);
    }

    const body = asResponseBody(response);
    const legacy = body?.chart_data;
    if (legacy && !Array.isArray(legacy)) {
        for (const group of legacy.bubble_groups ?? []) {
            for (const item of group.items ?? []) {
                addValue(item.y_value);
                addValue(item.bubble_size_value);
                for (const kpi of item.kpis ?? []) {
                    if (kpiIds.some((id) => kpiMatchesCandidate({ kpi_id: kpi.id } as PpaChartKpi, id))) {
                        addValue(kpi.value);
                    }
                }
            }
        }
    }

    return values;
};

/** Derives PA-07 KPI axis default min/max from loaded chart data for the selected X-axis KPI. */
export const extractKpiDataRangeFromChartData = (
    response: PpaChartResponse | null | undefined,
    kpiId: string,
    fallback: KpiDataRange = DEFAULT_KPI_DATA_RANGE,
): KpiDataRange => {
    const boundsFromApi = extractKpiAxisBoundsFromChartData(response, fallback);
    if (boundsFromApi) return boundsFromApi;

    const values = collectKpiNumericValues(response, kpiId);
    if (!values.length) return fallback;

    const rawMin = Math.min(...values);
    const rawMax = Math.max(...values);
    return toKpiDataRange(rawMin, rawMax);
};

const filterNodesByAttribute = (
    nodes: PpaChartDimensionNode[],
    attributeType?: string,
): PpaChartDimensionNode[] => {
    const normalizedAttributeType = normalizeAttributeKey(attributeType);
    if (!normalizedAttributeType) return nodes;

    const filteredNodes = nodes.filter(
        (node) => normalizeAttributeKey(node.attribute_type) === normalizedAttributeType,
    );

    // Meta axis id (e.g. "vendor") may not match API attribute_type casing/spelling.
    return filteredNodes.length > 0 ? filteredNodes : nodes;
};

/** Builds PA-07 axis preview items from accumulated chart rows (all loaded pages). */
export const buildAxisPreviewItemsFromRows = (
    rows: PpaChartDimensionNode[],
    attributeType?: string,
    salesKpiIds: SalesKpiIds = DEFAULT_SALES_KPI_IDS,
): AxisPreviewItem[] => buildAxisPreviewItemsFromChartData({ data: rows }, attributeType, salesKpiIds);

export const buildAxisPreviewItemsFromChartData = (
    response: PpaChartResponse | undefined | null,
    attributeType?: string,
    salesKpiIds: SalesKpiIds = DEFAULT_SALES_KPI_IDS,
): AxisPreviewItem[] => {
    const mapPreviewItem = toPreviewItem(salesKpiIds);
    const nodes = extractChartDimensionNodes(response);
    if (nodes.length > 0) {
        return filterNodesByAttribute(nodes, attributeType)
            .map(mapPreviewItem)
            .filter((item): item is AxisPreviewItem => item !== null);
    }

    const body = asResponseBody(response);
    const chartData = body?.chart_data;
    if (chartData && !Array.isArray(chartData)) {
        return buildPreviewItemsFromLegacyChartData(chartData, salesKpiIds, body?.table_data);
    }

    return [];
};

const VALUE_SALES_SHARE_KPI_IDS = ['value_sales_share'];
const VOLUME_SALES_SHARE_KPI_IDS = ['volume_sales_share'];

/** Reads show-top share KPIs from the chart API response (PA-07 axis preview). */
export const extractShowTopStatsFromChartResponse = (
    response: PpaChartResponse | null | undefined,
): ShowTopStats | null => {
    const body = asResponseBody(response);
    const kpis = body?.kpis;
    if (!kpis?.length) return null;

    const hasValueShare = hasKpiMatch(kpis, VALUE_SALES_SHARE_KPI_IDS);
    const hasVolumeShare = hasKpiMatch(kpis, VOLUME_SALES_SHARE_KPI_IDS);
    if (!hasValueShare && !hasVolumeShare) return null;

    return {
        valueSalesPercent: hasValueShare ? getKpiRawValue(kpis, VALUE_SALES_SHARE_KPI_IDS) : 0,
        volumeSalesPercent: hasVolumeShare ? getKpiRawValue(kpis, VOLUME_SALES_SHARE_KPI_IDS) : 0,
    };
};
